/**
 * @file This file contains the enhanced medication API functions.
 * @module api/medicationApi
 */

import { ENV } from '../../config/runtimeEnv';
import { getSessionUser } from "../../utils/auth/sessionUser";

// Types
import { Medication } from "../../types/healthcare/MedicationTypes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/**
 * Fetches all medications, optionally filtered by patient ID
 * @param patientId Optional ID of the patient to filter medications
 * @returns Promise with medication data
 */
export const fetchMedications = async (patientId?: UserUuid): Promise<{
	data: Medication[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/medications`,
		{
			params: {
				patient_id: patientId,
			},
		}
	);

	return {
		data: response.data,
	};
};

/**
 * Fetches a single medication by ID
 * @param id ID of the medication to fetch
 * @returns Promise with medication data
 */
export const fetchSingleMedicationById = async (
	id: number
): Promise<{
	data: Medication;
}> => {
	const response = await http.get(`${API_BASE_URL}/patients/medications/${id}`);
	return {
		data: response.data,
	};
};

/**
 * Creates a new medication record
 * @param medicationData Medication data to create
 * @returns Promise with created medication data and message
 */
export const setNewMedication = async (medicationData: Partial<Medication>): Promise<{
	data: Medication;
	message: string;
}> => {
	const user = getSessionUser();
	const response = await http.post(`${API_BASE_URL}/patients/medications`, {
		medicationData,
		user_id: user.uuid,
	});
	return {
		data: response.data.data || response.data,
		message: response.data.message || "Medication created successfully",
	};
};

/**
 * Updates an existing medication record
 * @param medicationData Medication data to update (must include id)
 * @returns Promise with updated medication data and message
 */
export const updateMedication = async (medicationData: Partial<Medication>): Promise<{
	data: Medication;
	message: string;
}> => {
	const user = getSessionUser();
	const response = await http.put(`${API_BASE_URL}/patients/medications/${medicationData.id}`, {
		medicationData,
		user_id: user.uuid,
	});
	return {
		data: response.data.data || response.data,
		message: response.data.message || "Medication updated successfully",
	};
};

/**
 * Deletes a medication record
 * @param medicationId ID of the medication to delete
 * @returns Promise with deletion result
 */
export const deleteMedication = async (medicationId: number) => {
	const response = await http.delete(`${API_BASE_URL}/patients/medications/${medicationId}`);
	return response.data;
};

/**
 * Fetches all active medications for a specific patient
 * @param patientId ID of the patient to fetch active medications for
 * @returns Promise with active medication data
 */
export const fetchActivePatientMedications = async (patientId: UserUuid): Promise<{
	data: Medication[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/${patientId}/active-medications`
	);

	return {
		data: response.data,
	};
};

/**
 * Fetches inactive medications for a specific patient
 * @param patientId ID of the patient to fetch inactive medications for
 * @returns Promise with inactive medication data
 */
export const fetchInactivePatientMedications = async (patientId: UserUuid): Promise<{
	data: Medication[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/${patientId}/inactive-medications`
	);

	return {
		data: response.data,
	};
};

/**
 * Fetches medications by category for a specific patient
 * @param patientId ID of the patient
 * @param category Medication category to filter by
 * @returns Promise with categorized medication data
 */
export const fetchMedicationsByCategory = async (patientId: UserUuid, category: string): Promise<{
	data: Medication[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/${patientId}/medications-by-category`,
		{
			params: {
				category
			},
		}
	);

	return {
		data: response.data,
	};
};

/**
 * Fetches medication statistics for a specific patient
 * @param patientId ID of the patient
 * @returns Promise with medication statistics
 */
export const fetchMedicationStats = async (patientId: UserUuid): Promise<{
	total: number;
	active: number;
	expiringSoon: number;
	interactions: number;
	highRisk: number;
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/${patientId}/medication-stats`
	);

	return response.data;
};

/**
 * Check for potential drug interactions with a medication
 * @param patientId ID of the patient
 * @param medicationId ID of the medication to check interactions for, or medication name if new
 * @returns Promise with interaction data
 */
export const checkMedicationInteractions = async (patientId: UserUuid, medicationId: number | string): Promise<{
	interactions: {
		medication1: string;
		medication2: string;
		severity: 'mild' | 'moderate' | 'severe';
		description: string;
	}[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/${patientId}/medication-interactions`,
		{
			params: {
				medication_id: typeof medicationId === 'number' ? medicationId : undefined,
				medication_name: typeof medicationId === 'string' ? medicationId : undefined
			},
		}
	);

	return response.data;
};

/**
 * Track medication adherence for a patient
 * @param patientId ID of the patient
 * @param medicationId ID of the medication
 * @param adherenceData Adherence data to record
 * @returns Promise with updated adherence data
 */
export const trackMedicationAdherence = async (
	patientId: UserUuid,
	medicationId: number,
	adherenceData: {
		date: Date;
		taken: boolean;
		notes?: string;
	}
): Promise<{
	data: any;
	message: string;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/patients/${patientId}/medications/${medicationId}/adherence`,
		{
			adherenceData
		}
	);

	return {
		data: response.data.data || response.data,
		message: response.data.message || "Adherence data recorded successfully",
	};
};

/**
 * Record a medication refill
 * @param medicationId ID of the medication
 * @param refillData Refill data to record
 * @returns Promise with updated refill data
 */
export const recordMedicationRefill = async (
	medicationId: number,
	refillData: {
		refill_date: Date;
		quantity?: number;
		pharmacy?: string;
		notes?: string;
	}
): Promise<{
	data: any;
	message: string;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/patients/medications/${medicationId}/refill`,
		{
			refillData
		}
	);

	return {
		data: response.data.data || response.data,
		message: response.data.message || "Refill recorded successfully",
	};
};

/**
 * Search medications by name, category, or other criteria
 * @param searchParams Search parameters
 * @returns Promise with search results
 */
export const searchMedications = async (searchParams: {
	patient_id?: UserUuid;
	name?: string;
	category?: string;
	active?: boolean;
	date_range?: [Date, Date];
}): Promise<{
	data: Medication[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/patients/medications/search`,
		{
			params: searchParams,
		}
	);

	return {
		data: response.data,
	};
};